import axios from 'axios';

// =============================================================================
// CHANGE LOG — Auth system overhaul  (May 2026)
// =============================================================================
//
// PROBLEMS FIXED:
//   1. Users had to log in every time they opened a new tab or came back after
//      2+ hours, even though the 30-day refresh token was still valid.
//   2. Having 2+ tabs open caused random logouts (refresh token rotation race).
//   3. After 2 hours mid-session the next API call would fail before recovering.
//
// WHAT CHANGED IN THIS FILE:
//
//   A. withCredentials: true added to the axios instance default.
//      Previously only login/logout set this per-request.  Without it on the
//      instance, any code path that bypassed the explicit option would silently
//      fail to send the refresh-token cookie.
//
//   B. isTokenExpired() + getTokenExpiry() exported.
//      Decode the JWT payload client-side (no network call) so AuthContext can
//      check expiry proactively at boot and schedule the background refresh
//      timer without waiting for a 401 from the server.
//
//   C. Cross-tab race-condition guard added to the 401 interceptor (two places):
//
//      PRE-REFRESH CHECK (line ~180):
//        Before calling /api/auth/refresh, read localStorage.  If another tab
//        already refreshed and stored a fresh token there, adopt it immediately
//        — no server call needed.  This handles the case where Tab A refreshed
//        before Tab B even enters the interceptor.
//
//      POST-REFRESH FAILURE RECOVERY (catch block, line ~214):
//        If our refresh call fails (because another tab refreshed first and the
//        backend's rotation logic blacklisted the token we sent), check
//        localStorage one final time.  If the winning tab left a fresh token,
//        adopt it and retry instead of logging the user out.
//
//      Without these two guards, the second tab to refresh always loses,
//      triggering a full logout for that tab.
//
// IF SOMETHING BREAKS HERE, CHECK:
//   - The /api/auth/refresh endpoint returns { access_token } and sets a new
//     refresh_token cookie.  If the cookie is missing the recovery guards above
//     will keep the user in using the localStorage token from the winning tab.
//   - The backend's CORS layer must NOT be overriding Access-Control-Allow-Origin
//     with "*" — browsers block cookies when the origin is a wildcard.
//     (Seen in prod: an Express proxy was adding "*" on top of FastAPI's correct
//     specific-origin header.  Fix that proxy or remove its CORS middleware.)
// =============================================================================

// ---------------------------------------------------------------------------
// JWT helpers — decode without verifying (signature check is the server's job)
// ---------------------------------------------------------------------------

/**
 * Returns true when the token is missing, malformed, or within 30 s of expiry.
 * The 30-second buffer absorbs minor clock skew between client and server.
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(window.atob(base64));
    return exp * 1000 < Date.now() + 30_000;
  } catch {
    return true;
  }
};

/**
 * Returns the token's expiry in milliseconds (epoch), or null on failure.
 */
export const getTokenExpiry = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(window.atob(base64));
    return exp * 1000;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 600_000,
  withCredentials: true, // Always send/receive cookies (required for refresh token)
});

// ---------------------------------------------------------------------------
// Asset URL resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a server asset path (e.g. "/api/event-assets/{id}") to a URL the
 * browser can load directly. <img> tags bypass the axios instance, so a
 * relative path has to be anchored to the same origin axios talks to.
 *
 * In dev the CRA proxy forwards /api to the backend, so a relative path works
 * as-is. In prod REACT_APP_API_URL is the absolute API origin, so we prefix it.
 * Absolute URLs (http/https/data) are returned untouched, which keeps the
 * contract intact when assets later move to S3/CDN.
 */
export const resolveAssetUrl = (path) => {
  if (!path) return '';
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  const base = process.env.REACT_APP_API_URL || '';
  return `${base}${path}`;
};

const _clamp01 = (n) => Math.max(0, Math.min(1, n));

/**
 * Encode a cover photo's focal point into its URL via a fragment
 * ("...#fp=x,y", normalized 0..1). A fragment is never sent to the server, so
 * the bytes stay cache-shared regardless of focus, and the focal point rides
 * along inside the single `coverImage` string (no extra event fields).
 * Passing a falsy focal (or center) returns the bare URL.
 */
export const buildCoverImageValue = (url, focal) => {
  if (!url) return url;
  const base = url.split('#')[0];
  if (!focal) return base;
  const x = _clamp01(Number(focal.x));
  const y = _clamp01(Number(focal.y));
  if (Number.isNaN(x) || Number.isNaN(y)) return base;
  return `${base}#fp=${x.toFixed(4)},${y.toFixed(4)}`;
};

/**
 * Resolve a stored cover photo string into what the DOM needs:
 *   { src, objectPosition, focal }
 * `src` is browser-loadable (fragment stripped so it never confuses caching),
 * `objectPosition` is the CSS value that focuses the crop on the chosen point,
 * `focal` is the normalized {x,y} (defaults to center).
 */
export const parseCoverImage = (coverImage) => {
  if (!coverImage) return { src: '', objectPosition: '50% 50%', focal: { x: 0.5, y: 0.5 } };
  const [rawUrl, frag] = String(coverImage).split('#');
  let focal = { x: 0.5, y: 0.5 };
  const m = frag && frag.match(/fp=([\d.]+),([\d.]+)/);
  if (m) focal = { x: _clamp01(parseFloat(m[1])), y: _clamp01(parseFloat(m[2])) };
  return {
    src: resolveAssetUrl(rawUrl),
    objectPosition: `${(focal.x * 100).toFixed(2)}% ${(focal.y * 100).toFixed(2)}%`,
    focal,
  };
};

// ---------------------------------------------------------------------------
// Event attachments (compliance materials: decks, pamphlets, PDFs, images)
//
// Unlike cover photos (public + cached), attachments are auth-gated, so the
// serve endpoint requires the Bearer header. That means <img>/<iframe> can't
// point at the URL directly — we fetch the bytes through the axios instance
// (which attaches the token) and render from an object URL the caller owns.
// ---------------------------------------------------------------------------

const _PPT_TYPES = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

/**
 * Classify an attachment by its content type into a previewable "kind":
 *   'image' | 'pdf' | 'ppt' | 'other'
 * Drives both the row icon and whether an in-app preview is possible.
 */
export const attachmentKind = (contentType, name = '') => {
  const ct = String(contentType || '').toLowerCase();
  const ext = String(name || '').toLowerCase().split('.').pop();
  if (ct.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  if (ct === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (_PPT_TYPES.includes(ct) || ['ppt', 'pptx'].includes(ext)) return 'ppt';
  return 'other';
};

/** True when an attachment can be rendered inline (image or PDF). */
export const isAttachmentPreviewable = (contentType, name = '') =>
  ['image', 'pdf'].includes(attachmentKind(contentType, name));

/**
 * Fetch an auth-gated attachment as a blob and return a browser object URL plus
 * its resolved content type. The CALLER is responsible for revoking the object
 * URL (URL.revokeObjectURL) when done, to avoid leaking memory.
 */
export const fetchAttachmentObjectUrl = async (url) => {
  const { data, headers } = await api.get(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(data);
  return { objectUrl, contentType: headers?.['content-type'] || data.type || '' };
};

/**
 * Download an auth-gated attachment to the user's device. Fetches the bytes via
 * axios (so the token is attached), then triggers a save through a transient
 * anchor. The temporary object URL is revoked immediately after the click.
 */
export const downloadAttachment = async (url, filename = 'attachment') => {
  const { data } = await api.get(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(data);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

// ---------------------------------------------------------------------------
// AuthContext callback bridge
// ---------------------------------------------------------------------------

let authCallbacks = {
  onTokenRefreshed: null,
  onTokenRefreshFailed: null,
};

export const setAuthCallbacks = (callbacks) => {
  authCallbacks = { ...authCallbacks, ...callbacks };
};

// ---------------------------------------------------------------------------
// Request interceptor — attach Bearer token or anonymous header
// ---------------------------------------------------------------------------

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      const anonymousUserId = localStorage.getItem('anonymous_user_id');
      if (anonymousUserId) {
        config.headers['X-Anonymous-User-Id'] = anonymousUserId;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor — silent token refresh on 401
// ---------------------------------------------------------------------------

let isRefreshing = false;
let failedQueue = [];
let refreshPromise = null;

// Export so SSE handlers can coordinate with an in-progress refresh.
export const getRefreshState = () => ({ isRefreshing, refreshPromise });

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

/**
 * Recover silently by adopting `token` from localStorage when another tab
 * already completed a refresh (handles the RT rotation race condition).
 */
const recoverWithLatestToken = (token, originalRequest) => {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  authCallbacks.onTokenRefreshed?.(token);
  processQueue(null, token);
  originalRequest.headers['Authorization'] = `Bearer ${token}`;
  isRefreshing = false;
  refreshPromise = null;
  return api(originalRequest);
};

// Helper to extract feature name for anonymous signup prompts
const extractFeatureFromUrl = (url) => {
  if (url.includes('/enrich')) return 'contact enrichment';
  if (url.includes('/export')) return 'data export';
  if (url.includes('/custom-fields/campaigns') && url.includes('/generate')) return 'custom fields';
  if (url.includes('/custom-fields/campaigns') && url.includes('/approve')) return 'custom fields';
  if (url.includes('/verify') || url.includes('/emails')) return 'email verification';
  if (url.includes('/preview-token-spend')) return 'credit calculation';
  return 'this feature';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // -----------------------------------------------------------------------
    // 403 — anonymous user needs to create an account
    // -----------------------------------------------------------------------
    const requiresSignup =
      error.response?.headers?.['x-requires-signup'] ||
      error.response?.headers?.get?.('x-requires-signup');

    if (error.response?.status === 403 && requiresSignup === 'true') {
      const feature = extractFeatureFromUrl(originalRequest.url);
      window.dispatchEvent(
        new CustomEvent('anonymousUserNeedsSignup', { detail: { feature } })
      );
      return Promise.resolve({ data: null, status: 403, requiresSignup: true, feature });
    }

    // -----------------------------------------------------------------------
    // 401 — attempt silent token refresh (once per request)
    // -----------------------------------------------------------------------
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't refresh on login failures or when there is no token at all
    if (
      originalRequest.url.includes('/api/auth/login') ||
      !localStorage.getItem('token')
    ) {
      console.debug('[Auth] 401 on unauthenticated or login request — skipping refresh');
      return Promise.reject(error);
    }

    // -----------------------------------------------------------------------
    // Queue concurrent requests while a refresh is already in-flight
    // -----------------------------------------------------------------------
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    // -----------------------------------------------------------------------
    // Cross-tab guard — another tab may have already refreshed the token.
    // If localStorage now holds a fresh token we never need to hit the server.
    // -----------------------------------------------------------------------
    const preCheckToken = localStorage.getItem('token');
    if (preCheckToken && !isTokenExpired(preCheckToken)) {
      return recoverWithLatestToken(preCheckToken, originalRequest);
    }

    // -----------------------------------------------------------------------
    // Perform the actual token refresh
    // -----------------------------------------------------------------------
    try {
      refreshPromise = api.post('/api/auth/refresh', {}, {
        // _retry prevents an infinite refresh loop if the refresh endpoint itself 401s
        _retry: true,
      });

      const { data } = await refreshPromise;
      const newToken = data.access_token;

      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      authCallbacks.onTokenRefreshed?.(newToken);
      processQueue(null, newToken);

      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      isRefreshing = false;
      refreshPromise = null;
      return api(originalRequest);

    } catch (refreshError) {
      // -------------------------------------------------------------------
      // Last-chance cross-tab recovery.
      // Our refresh request may have lost the rotation race: another tab
      // refreshed first (blacklisting the token we sent), but it left a
      // fresh token in localStorage.  Adopt it instead of logging out.
      // -------------------------------------------------------------------
      const recoveryToken = localStorage.getItem('token');
      if (recoveryToken && !isTokenExpired(recoveryToken)) {
        return recoverWithLatestToken(recoveryToken, originalRequest);
      }

      // Genuine refresh failure — clear session and redirect to login
      processQueue(refreshError, null);
      isRefreshing = false;
      refreshPromise = null;

      localStorage.removeItem('token');
      localStorage.removeItem('anonymous_user_id');

      authCallbacks.onTokenRefreshFailed?.();
      window.location.href = '/login';

      return Promise.reject(refreshError);
    }
  }
);

export default api;
